import { auth, db } from './firebase';
import {
  addMockProviderRecord,
  archiveMockProviderRecord,
  getMockProvidersForChild,
  isE2EMockEnabled,
  updateMockProviderRecord,
} from './e2eMock';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import {
  PROVIDER_ROLE_VALUES,
  PROVIDER_SOURCE_KINDS,
} from '../constants/providers';

const normalizeProviderSource = (source = {}) => ({
  kind: PROVIDER_SOURCE_KINDS.includes(source.kind) ? source.kind : 'manual',
  sourceId: source.sourceId || null,
  sourceField: source.sourceField || null,
  sourceLabel: source.sourceLabel || null,
  sourceText: source.sourceText || null,
  confidence: source.confidence || null,
});

export const normalizeProvider = (provider = {}) => ({
  id: provider.id || '',
  childId: String(provider.childId || '').trim(),
  name: String(provider.name || '').trim(),
  role: PROVIDER_ROLE_VALUES.includes(provider.role) ? provider.role : 'other',
  specialty: String(provider.specialty || '').trim(),
  organizationName: String(provider.organizationName || '').trim(),
  phone: String(provider.phone || '').trim(),
  email: String(provider.email || '').trim(),
  address: String(provider.address || '').trim(),
  notes: String(provider.notes || '').trim(),
  isActive: provider.isActive !== false,
  source: normalizeProviderSource(provider.source || {}),
  createdAt: provider.createdAt || null,
  updatedAt: provider.updatedAt || null,
  archivedAt: provider.archivedAt || null,
});

export const fetchProviders = async (childId) => {
  try {
    if (isE2EMockEnabled()) {
      return getMockProvidersForChild(childId).map(normalizeProvider);
    }

    const q = query(
      collection(db, 'providers'),
      where('childId', '==', childId)
    );

    const querySnapshot = await getDocs(q);
    const providers = [];
    querySnapshot.forEach((providerDoc) => {
      providers.push(normalizeProvider({ id: providerDoc.id, ...providerDoc.data() }));
    });
    return providers;
  } catch (error) {
    console.error('Error fetching providers:', error);
    throw error;
  }
};

export const addProvider = async (providerData) => {
  try {
    if (isE2EMockEnabled()) {
      return addMockProviderRecord(providerData).id;
    }

    const currentUserId = auth.currentUser?.uid;
    if (!currentUserId) {
      throw new Error('You must be signed in to add a provider.');
    }

    // The document id belongs to the UI model, not the stored provider fields.
    const { id, ...normalized } = normalizeProvider(providerData);
    const now = serverTimestamp();
    const docRef = await addDoc(collection(db, 'providers'), {
      ...normalized,
      source: normalizeProviderSource(providerData.source || {}),
      createdAt: now,
      updatedAt: now,
      archivedAt: normalized.isActive ? null : now,
    });

    return docRef.id;
  } catch (error) {
    console.error('Error adding provider:', error);
    throw error;
  }
};

export const updateProvider = async (providerId, updatedData) => {
  try {
    if (isE2EMockEnabled()) {
      updateMockProviderRecord(providerId, updatedData);
      return;
    }

    const providerRef = doc(db, 'providers', providerId);
    const existingSnapshot = await getDoc(providerRef);
    const existingProvider = existingSnapshot.exists() ? existingSnapshot.data() : {};
    const normalized = normalizeProvider(updatedData);
    const {
      id,
      createdAt,
      updatedAt,
      archivedAt,
      ...editableFields
    } = normalized;

    await updateDoc(providerRef, {
      ...editableFields,
      source: normalizeProviderSource(updatedData.source || normalized.source || {}),
      createdAt: existingProvider.createdAt || createdAt || serverTimestamp(),
      archivedAt: existingProvider.archivedAt ?? archivedAt ?? null,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating provider:', error);
    throw error;
  }
};

export const archiveProvider = async (providerId) => {
  try {
    if (isE2EMockEnabled()) {
      archiveMockProviderRecord(providerId);
      return;
    }

    const providerRef = doc(db, 'providers', providerId);
    await updateDoc(providerRef, {
      isActive: false,
      archivedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error archiving provider:', error);
    throw error;
  }
};
